import './Membership.scss'

import { useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { Button, Card, Col, Row, Spinner } from 'react-bootstrap'

import { app } from '../../../config'
import { navigateTo } from 'components/common/navigation'
import {
  ROUTE_MEMBERSHIP,
  ROUTE_PAYMENTS_DETAILS,
} from 'components/common/helpers/Paths'
import { getSubscriptionPlans } from 'components/common/api/Payments'
import { trackGAEvent, trackMetaEvent } from 'components/common/api/UserEvents'
import { buildRequestURL } from 'components/common/api'
import moment from 'moment'

/**
 * Simple Membership component. Used to display a membership CTA with a simple URI trigger
 */
export default function Membership({
  className = '',
  variant = '',
  defaultShow = true,
  subscription = null,
  returnPath = null
}) {
  const history = useHistory()
  const [plans, setPlans] = useState()
  const [monthlySubscriptionCost, setMonthlySubscriptionCost] = useState()
  const [annualSubscriptionCost, setAnnualSubscriptionCost] = useState()
  const [isLoading, setIsLoading] = useState(true)
  const ref = useRef()

  /**
   * Simple helper function to return the annual discount percentage
   */
  const _annualDiscount = () =>
    Math.floor((annualSubscriptionCost / (monthlySubscriptionCost * 12)) * 100)

  /**
   * Simple helper to get trial expiration message
   * @returns {string}
   */
  const _getTrialExpirationMessage = end_date => {
    if (!subscription?.ends_on) return '7-day Trial. Unlimited Access.'

    const daysRemaining = Math.ceil(
      moment(subscription.ends_on).diff(moment(), 'days', true)
    )

    return daysRemaining > 0
      ? `Your trial ends in ${daysRemaining} days`
      : 'Your trial has expired.'
  }

  useEffect(() => {
    ;(async function getPlansAsync() {
      setIsLoading(true)

      try {
        // Get our plan details
        const featuredMonthlyPlanId = parseInt(app.plans.featured_ids.monthly)
        const featuredAnnualPlanId = parseInt(app.plans.featured_ids.annual)
        const subscriptionPlans = await getSubscriptionPlans()

        // Filter out to get the ones we want
        const featuredMonthlyPlan = subscriptionPlans.find(
          plan => plan.id === featuredMonthlyPlanId
        )
        const featuredAnnualPlan = subscriptionPlans.find(
          plan => plan.id === featuredAnnualPlanId
        )

        // If we've unmounted the component, don't do anything
        if (!ref.current) return

        // Set it and forget it
        setMonthlySubscriptionCost(parseFloat(featuredMonthlyPlan.cost))
        setAnnualSubscriptionCost(parseFloat(featuredAnnualPlan.cost))
        setPlans(subscriptionPlans)
        setIsLoading(false)
      } catch (e) {
        console.error(e)
      } // Swallow exceptions
    })()
  }, [])

  // If we're not showing the membership, don't render anything
  return !defaultShow ? null : (
    <div ref={ref} className={`membership-wrapper ${className} ${variant}`}>
      {variant === 'small' ? (
        <Row>
          <Col xs={12} md={6}>
            <h2 className='green'>Remble Membership</h2>
            <h4 className='my-2'>
              {_getTrialExpirationMessage(subscription?.ends_on)}
            </h4>
          </Col>
          <Col xs={12} md={6}>
            <Button
              className='mt-3'
              size='lg'
              variant='primary'
              onClick={() => {
                // Track to Meta
                trackMetaEvent('AddToCart', {
                  content_name: 'Clicked Subscribe Now'
                })

                // Track to GA
                trackGAEvent(
                  'add_to_cart',
                  'add_to_cart',
                  'Clicked Subscribe Now'
                )

                // Do we have a returnPath to use from the query string?
                const _returnPath =
                  returnPath
                    ? returnPath
                    : new URLSearchParams(
                        history.location.search
                      ).get('returnPath')

                // Build the url
                const url = buildRequestURL(ROUTE_MEMBERSHIP, { returnPath: _returnPath })

                // Navigate to the membership page
                navigateTo(url, history)
              }}
            >
              <span>Subscribe Now</span>
            </Button>
          </Col>
        </Row>
      ) : (
        <>
          {/* No subscription, let's show them the option to upgrade */}
          <h1 className='green'>Get Remble Membership</h1>
          <Card className='membership-card border-0'>
            <Card.Title>
              <h3>All the access, all the time</h3>
            </Card.Title>
            {isLoading || !plans ? (
              <Spinner
                className='spinner'
                animation='border'
                role='status'
                variant='primary'
                size='lg'
              >
                <span className='visually-hidden'>Getting prices...</span>
              </Spinner>
            ) : (
              <Card.Subtitle>
                {monthlySubscriptionCost && (
                  <h4>${monthlySubscriptionCost} Monthly</h4>
                )}
                {annualSubscriptionCost && (
                  <>
                    <h5>${annualSubscriptionCost} Yearly</h5>
                    <h5 className='discount'>{_annualDiscount()}% off!</h5>
                  </>
                )}
              </Card.Subtitle>
            )}
            <Button
              size='lg'
              variant='outline-primary'
              className='mt-5'
              onClick={() => {
                // Track to Meta
                trackMetaEvent('AddToCart', {
                  content_name: 'Clicked Subscribe'
                })

                // Track to GA
                trackGAEvent('add_to_cart', 'add_to_cart', 'Clicked Subscribe')

                navigateTo(ROUTE_PAYMENTS_DETAILS, history)
              }}
            >
              <span>I'm ready to subscribe</span>
            </Button>
          </Card>
        </>
      )}
    </div>
  )
}
